import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { getCycleDetails } from '../../../store/actions/financialActions';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Chip,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import { format, parseISO, isValid } from 'date-fns';

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : '-';
    } catch {
        return '-';
    }
};

interface CycleDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    cycleId: number | null;
    cycleLote?: number;
}

const CycleDetailsDialog: React.FC<CycleDetailsDialogProps> = ({ open, onClose, cycleId, cycleLote }) => {
    const dispatch = useDispatch();
    const cycleDetails = useSelector((state: RootState) => state.financial.cycleDetails);
    const isFetchingDetails = useSelector((state: RootState) => state.financial.isFetchingDetails);

    useEffect(() => {
        if (open && cycleId) {
            // @ts-ignore
            dispatch(getCycleDetails(cycleId));
        }
    }, [open, cycleId, dispatch]);

    const installmentsData = useMemo(() =>
        cycleDetails
            .filter((d: any) => d.installments)
            .map((d: any) => ({
                PaymentCard: d.installments?.enrollment?.PaymentCard || '-',
                NumberInEnrollment: d.installments?.NumberInEnrollment || '-',
                PayDate: d.installments?.PayDate || null,
                Amount: d.installments?.Amount || 0,
                Discount: d.installments?.Discount || 0,
                Acrescimos: (d.installments?.Interest || 0) + (d.installments?.Fee || 0),
                TotalPayment: d.installments?.TotalPayment || 0,
                FormaPagto: d.paymentTypes?.Description || '-',
                UniqueControl: d.installments?.UniqueControl || '-',
            })),
        [cycleDetails]
    );

    const otherData = useMemo(() =>
        cycleDetails
            .filter((d: any) => !d.installments)
            .map((d: any) => ({
                isCredit: d.isCredit,
                Description: d.Description || '-',
                Amount: d.Amount || 0,
                Person: d.person?.Name || '-',
                FormaPagto: d.paymentTypes?.Description || '-',
            })),
        [cycleDetails]
    );

    const totalInstallments = installmentsData.reduce(
        (sum: number, d: any) => sum + d.TotalPayment, 0
    );

    const totalOtherIncome = otherData
        .filter((d: any) => d.isCredit)
        .reduce((sum: number, d: any) => sum + d.Amount, 0);

    const totalOtherExpense = otherData
        .filter((d: any) => !d.isCredit)
        .reduce((sum: number, d: any) => sum + d.Amount, 0);

    const installmentColumns: MUIDataTableColumnDef[] = [
        {
            name: 'PaymentCard',
            label: 'Carnê',
            options: { sort: true, filter: true }
        },
        {
            name: 'NumberInEnrollment',
            label: 'Mês',
            options: { sort: true }
        },
        {
            name: 'PayDate',
            label: 'DH Pagto',
            options: {
                sort: true,
                customBodyRender: (value: string) => formatDateTime(value)
            }
        },
        {
            name: 'Amount',
            label: 'Valor',
            options: {
                sort: true,
                customBodyRender: (value: number) => formatBRL(value)
            }
        },
        {
            name: 'Discount',
            label: 'Desconto',
            options: {
                sort: true,
                customBodyRender: (value: number) => formatBRL(value)
            }
        },
        {
            name: 'Acrescimos',
            label: 'Acréscimos',
            options: {
                sort: true,
                customBodyRender: (value: number) => formatBRL(value)
            }
        },
        {
            name: 'TotalPayment',
            label: 'Total Pago',
            options: {
                sort: true,
                customBodyRender: (value: number) => formatBRL(value)
            }
        },
        {
            name: 'FormaPagto',
            label: 'Forma Pagto',
            options: { sort: true }
        },
        {
            name: 'UniqueControl',
            label: 'Controle Único',
            options: { sort: true }
        }
    ];

    const otherColumns: MUIDataTableColumnDef[] = [
        {
            name: 'isCredit',
            label: 'Operação',
            options: {
                sort: true,
                customBodyRender: (value: boolean) => (
                    <Chip
                        label={value ? 'Receita' : 'Despesa'}
                        color={value ? 'success' : 'error'}
                        size="small"
                    />
                )
            }
        },
        {
            name: 'Description',
            label: 'Descrição',
            options: { sort: true }
        },
        {
            name: 'Amount',
            label: 'Valor',
            options: {
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = otherData[dataIndex];
                    const val = row?.isCredit ? row.Amount : row.Amount * -1;
                    return (
                        <Typography
                            sx={{ color: row?.isCredit ? 'success.main' : 'error.main', fontWeight: 500 }}
                        >
                            {formatBRL(val)}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'Person',
            label: 'Pessoa',
            options: { sort: true }
        },
        {
            name: 'FormaPagto',
            label: 'Forma Pagto',
            options: { sort: true }
        }
    ];

    const baseTableOptions = {
        filterType: 'dropdown' as const,
        responsive: 'standard' as const,
        selectableRows: 'none' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        download: false,
        print: false,
        viewColumns: false,
        textLabels: {
            body: {
                noMatch: 'Sem registros',
                toolTip: 'Ordenar',
            },
            pagination: {
                next: 'Próxima Página',
                previous: 'Página Anterior',
                rowsPerPage: 'Registros por página:',
                displayRows: 'de',
            },
            toolbar: {
                search: 'Pesquisar',
                filterTable: 'Filtrar',
            },
            filter: {
                all: 'Todos',
                title: 'Filtros',
                reset: 'Reiniciar',
            },
        },
    };

    const installmentTableOptions = {
        ...baseTableOptions,
        search: true,
        filter: true,
        searchPlaceholder: 'Pesquisar por carnê...',
    };

    const otherTableOptions = {
        ...baseTableOptions,
        search: false,
        filter: false,
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Detalhes do Ciclo de Caixa - Lote #{cycleLote || cycleId}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isFetchingDetails ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ mb: 3 }}>
                            <MUIDataTable
                                title={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Mensalidades
                                        </Typography>
                                        <Chip
                                            label={`Total: ${formatBRL(totalInstallments)}`}
                                            color="primary"
                                            size="small"
                                        />
                                    </Box>
                                }
                                data={installmentsData}
                                columns={installmentColumns}
                                options={installmentTableOptions}
                            />
                        </Box>

                        <Box>
                            <MUIDataTable
                                title={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Outras Receitas e Despesas
                                        </Typography>
                                        <Chip
                                            label={`Receitas: ${formatBRL(totalOtherIncome)}`}
                                            color="success"
                                            size="small"
                                        />
                                        <Chip
                                            label={`Despesas: ${formatBRL(totalOtherExpense)}`}
                                            color="error"
                                            size="small"
                                        />
                                    </Box>
                                }
                                data={otherData}
                                columns={otherColumns}
                                options={otherTableOptions}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CycleDetailsDialog;
